import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  TablePagination
} from "@mui/material";
import { NavLink } from "react-router-dom";
import SurveySlackCardItem from "./survey-card-item";
import SurveyTableSearch from "../survey-table-search";
import { useTranslation } from "react-i18next";
import ErrorBoundary from "../../../../azure_auth/ErrorBoundary";
import { connect } from "react-redux";

/**
 * SurveySlackCard:
 * - Muestra búsqueda, paginación y listado de encuestas.
 * - Envuelto en ErrorBoundary para capturar errores generales.
 */
const SurveySlackCard = (props) => {
  const {
    /* desde Redux ---------------------------------- */
    user,
    batabotAccess,

    /* desde el contenedor -------------------------- */
    status,
    surveys,
    sendSurveyToTeam,
    sending,
    deleteSurvey,
  } = props;

  const [order, setOrder] = useState("desc");
  const [filter, setFilter] = useState("");
  const [orderBy, setOrderBy] = useState("createdAt");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(6);
  const { t } = useTranslation();

  /* ----------- 1 · Normalizar batabotAccess ---------------- */
  const normalizedAccess = useMemo(() => {
    if (Array.isArray(batabotAccess)) {
      return batabotAccess.map((e) => e.trim().toLowerCase());
    }
    if (typeof batabotAccess === "string") {
      // intenta parsear JSON
      try {
        const parsed = JSON.parse(batabotAccess);
        if (Array.isArray(parsed)) {
          return parsed.map((e) => e.trim().toLowerCase());
        }
      } catch (_) {
        // divide por coma si no es JSON válido
        return batabotAccess
          .split(",")
          .map((e) => e.replace(/[\[\]'"]/g, "").trim().toLowerCase());
      }
    }
    return [];
  }, [batabotAccess]);

  /* ----------- 2 · Calcular whitelist --------------------- */
  const isUserWhitelisted = normalizedAccess.includes(
    (user?.email || "").trim().toLowerCase()
  );

  /* ----------- 3 · Logs de depuración -------------------- */
  useEffect(() => {
    console.log("********** isUserWhitelisted:", isUserWhitelisted);
    console.log("   → user:", user);
    console.log("   → batabotAccess:", batabotAccess);
    console.log("   → normalizedAccess:", normalizedAccess);
  }, [user, batabotAccess, normalizedAccess, isUserWhitelisted]);

  /* ----------- Handlers de orden, filtro, paginación ------ */
  const handleRequestSort = (_e, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };
  const handleChangePage = (_e, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };
  const handleChangeFilter = (value) => setFilter(value);

  /* ----------- Ordenar, filtrar, paginar ------------------ */
  const filteredSorted = stableSort(
    surveys,
    getComparator(order, orderBy),
    filter
  );
  const paginated = filteredSorted.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <ErrorBoundary sectionName="SurveySlackCard">
      <Box sx={{ width: "100%" }}>
        {/* Botón crear encuesta */}
        <Box className="py-3">
          <NavLink to="/survey-create" style={{ textDecoration: "none" }}>
            <Button variant="contained">{t("surveys.newSurvey")}</Button>
          </NavLink>
        </Box>

        <Box className="my-3">
          {/* Buscador */}
          <SurveyTableSearch setFilter={handleChangeFilter} />

          {/* Listado o loader */}
          {status === "loading" ? (
            <Box className="text-center col-12" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', paddingTop: '80px' }}>
              <CircularProgress size={40} thickness={4} />
            </Box>
          ) : (
            <Box className="row">
              {paginated.map((item, index) => (
                <ErrorBoundary
                  key={item.id || index}
                  sectionName={`SurveySlackCardItem-${item.id || index}`}
                >
                  <Box className="col-md-6 my-2">
                    <SurveySlackCardItem
                      item={item}
                      sendSurveyToTeam={sendSurveyToTeam}
                      deleteSurvey={deleteSurvey}
                      sending={sending}
                      /* NUEVOS props --------------------------- */
                      isUserWhitelisted={isUserWhitelisted}
                      tenantId={user?.tenantId}
                    />
                  </Box>
                </ErrorBoundary>
              ))}
            </Box>
          )}

          {/* Paginación */}
          <TablePagination
            className="child-mb-0"
            rowsPerPageOptions={[6, 10, 24]}
            component="div"
            count={surveys.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      </Box>
    </ErrorBoundary>
  );
};

/* ---------- Utilidades de orden y filtrado ---------------- */
function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}
function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}
function stableSort(array, comparator, filter) {
  const filtered = array.filter((el) =>
    filter === ""
      ? true
      : el.title.toLowerCase().includes(filter.toLowerCase())
  );
  const stabilized = filtered.map((el, index) => [el, index]);
  stabilized.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilized.map((el) => el[0]);
}

/* ---------- Redux connection ------------------------------ */
const mapStateToProps = (state) => ({
  user: Array.isArray(state.users.user)
    ? state.users.user[0]
    : state.users.user,
  batabotAccess: state.batabot.access,
});
export default connect(mapStateToProps)(SurveySlackCard);
